import type { ComponentType } from "react";
import { BiMobileAlt, BiLaptop, BiDesktop } from "react-icons/bi";
import { MdTablet } from "react-icons/md";

export interface DeviceInfo {
  IconComponent: ComponentType<{ className?: string }>;
  name: string;
}

/**
 * Parse device summary string to extract simplified device info
 * Examples:
 * - "Chrome 143.0.0.0 on macOS 10.15.7" -> "Chrome on macOS"
 * - "Safari on iPhone" -> "Safari on iPhone"
 * - "Chrome 143.0.0.0 on Windows 10" -> "Chrome on Windows"
 */
export const parseDeviceSummary = (deviceSummary: string | null | undefined): DeviceInfo | null => {
  if (!deviceSummary) return null;

  const lower = deviceSummary.toLowerCase();

  // Detect device type for icon
  let IconComponent: ComponentType<{ className?: string }>;
  if (
    lower.includes("mobile") ||
    lower.includes("phone") ||
    lower.includes("iphone") ||
    lower.includes("android phone")
  ) {
    IconComponent = BiMobileAlt;
  } else if (
    lower.includes("tablet") ||
    lower.includes("ipad") ||
    lower.includes("android tablet")
  ) {
    IconComponent = MdTablet;
  } else if (lower.includes("laptop")) {
    IconComponent = BiLaptop;
  } else {
    IconComponent = BiDesktop;
  }

  // Extract browser and OS names (simplified)
  // Pattern: "Browser Version on OS Version" -> "Browser on OS"
  const parts = deviceSummary.split(" on ");
  if (parts.length >= 2) {
    const browserPart = parts[0].trim();
    const osPart = parts[1].trim();

    // Extract browser name (remove version numbers)
    // Handle cases like "Chrome 143.0.0.0" -> "Chrome"
    const browserWords = browserPart.split(/\s+/);
    const browserName = browserWords[0];

    // Extract OS name (remove version numbers)
    // Handle cases like "macOS 10.15.7" -> "macOS", "Windows 10" -> "Windows"
    const osWords = osPart.split(/\s+/);
    // Take first word for OS (macOS, Windows, Linux, etc.)
    const osName = osWords[0];

    return {
      IconComponent,
      name: `${browserName} on ${osName}`,
    };
  }

  // Fallback: if no "on" separator, try to extract just the browser name
  const firstWord = deviceSummary.split(/\s+/)[0];
  return {
    IconComponent,
    name: firstWord || deviceSummary,
  };
};
